import appID from '../api/appID';
import { NetworkActionPrefix, ActionType, HTTPStatusCode } from '../utils/constants';
import { isInternalID, stripInternalsFromAPIArguments, unwrapAPIResponse } from '../api/utils';
import * as Sentry from '@sentry/react';

// apparently Swagger client doesn't use prototypes in it's classes, but assigns all properties and methods in constructors of APIs classes
// so each 'new SomeStuffApi()' call is a recreation of all of it's methods
// but these instances are nothing but a set of functions, it's better to create it once and then reuse it
// so here in networkAPIAction we'll expect API class instead of an instance, create an instance if/when needed and put it to global cache for later reuse
// it's pretty much the same approach we already have in most of action files, 
// but slightly more centralized, shorter to write and friendlier to nested/consequtive actions/dispatches/Promises/whatever
// TODO actions with the same API, method and prefix should work the same, is it possible an useful to cache them as well?
const APIInstances = new WeakMap();
const getAPIInstance = apiClassOrInstance =>
    typeof apiClassOrInstance !== 'function' ? apiClassOrInstance : // TODO always pass classes and remove this check
        !APIInstances.has(apiClassOrInstance) ? APIInstances.set(apiClassOrInstance, new apiClassOrInstance()).get(apiClassOrInstance) :
            APIInstances.get(apiClassOrInstance);


function assertAPIEntryExists(entityMiffix, apiClassOrInstance, name) {
    if (!getAPIInstance(apiClassOrInstance)[name]) {
        throw new Error(`API entry ${entityMiffix}.${name} does not exist.`);
    }
};

function assertPrefixExists(name) {
    if (!NetworkActionPrefix[name]) {
        throw new Error(`Constant ${name} does not exist.`);
    }
};

function assertActionExists(name) {
    if (!ActionType[name]) {
        throw new Error(`Constant ${name} does not exist.`);
    }
};

function handleApiResponse(dispatch, typePrefix, methodName, actionPayload, error, response, resolve, reject) {
    if (error) {
        if (error.status === HTTPStatusCode.UNAUTHORIZED) {
            dispatch({ type: ActionType.VOID_SESSION });
        }

        if (!error.timeout) {
            dispatch({ type: ActionType.FAILED_API_RESPONSE, message: error.response && error.response.body && error.response.body.UIMessage });
        }

        dispatch({ type: ActionType[`${typePrefix}_FAIL`], error, ...actionPayload });
        if (typeof reject === 'function') {
            // rejected Promise without onReject handler throws an error
            // we already handle request fails with couple of actions to show nice error notification

            // previously networkAPIAction's Promise had empty onReject handler, but it caused all errors to be suppressed
            // and made all promises resolved regardless of actual outcome (so all components should already work with empty resolve argument)
            // we can not reject the Promise since a Promise with no onRejected handler throws an error and it might break redux loop and therefore error notification
            // we should not keep the Promise unfulfilled since there can be memory leaks
            // TODO some future components might want to know if request has failed with Promise API, find a way to pass some errorness flag without throwing an error when there's no onReject

            resolve(null);
        }

        if (!DEBUG && !error.timeout) {
            Sentry.captureException(error);
        }
    }
    else {
        if (response === null || response === undefined) {
            response = null;
        } else {
            response = unwrapAPIResponse(response) || response;
        }

        dispatch({ type: ActionType[`${typePrefix}_SUCCESS`], response, ...actionPayload });
        if (typeof resolve === 'function') {
            resolve(response);
        }
    }
}

function plural(s) {
    const last = s[s.length - 1];
    switch (last) {
        case 's': return s + 'es';
        case 'S': return s + 'ES';
        case 'y': return s.slice(0, -1) + 'ies';
        case 'Y': return s.slice(0, -1) + 'IES';
        default:
            return last.toLowerCase() === last ? s + 's' : s + 'S';
    }
}

export function networkAPIAction(apiClassOrInstance, method, typePrefix, actionPayload, ...apiMethodArgs) {
    apiClassOrInstance = getAPIInstance(apiClassOrInstance);

    return dispatch => new Promise((resolve, reject) => {
        dispatch({ type: ActionType[`${typePrefix}_PENDING`], ...actionPayload });

        apiClassOrInstance[method](appID, ...stripInternalsFromAPIArguments(apiMethodArgs), (error, response) => handleApiResponse(dispatch, typePrefix, method, actionPayload, error, response, resolve, reject));
    });
};

const latestDroppableAPIAction = {};

export function droppableNetworkAPIAction(apiClassOrInstance, method, typePrefix, actionPayload, ...apiMethodArgs) {
    apiClassOrInstance = getAPIInstance(apiClassOrInstance);

    return dispatch => new Promise((resolve, reject) => {
        // TODO pass action id generator, there might be the same action working with different parts of store based on entity ID or something
        // currently a very dirty hack for multiple newsfeeds
        const actionTypeID = typePrefix + (actionPayload ? actionPayload.newsfeedKey || actionPayload.erpSyncProductsKey || actionPayload.erpSyncCustomersKey || '' : '');

        const actionID = latestDroppableAPIAction[actionTypeID] = (Math.random() * Number.MAX_SAFE_INTEGER) | 0;
        dispatch({ type: ActionType[`${typePrefix}_PENDING`], ...actionPayload });

        apiClassOrInstance[method](appID, ...stripInternalsFromAPIArguments(apiMethodArgs), (error, response) => {
            if (latestDroppableAPIAction[actionTypeID] !== actionID) {
                // dropped actions should be ignored, but unfulfilled promise might lead to memory leaks and bad stuff when subscribed component unmounts
                // it should not be rejected because uncaught rejection throws errors, but this is not error
                // check huge comment in handleApiResponse
                resolve(null);
                return;
            }
            latestDroppableAPIAction[actionTypeID] = null;

            handleApiResponse(dispatch, typePrefix, method, actionPayload, error, response, resolve, reject);
        });
    });
};

export function dropNetworkAPIAction(typePrefix) {
    latestDroppableAPIAction[typePrefix] = null;
};

/**
 * Creates a set of most used action creators for an entity.
 *      loadAllSomeEntities(...args) -> {type: LOAD_ALL_SOME_ENTITIES}
 *      loadSomeEntity(someEntityID, ...args) -> {type: LOAD_SOME_ENTITY, someEntityID}
 *      addSomeEntity(someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: ADD_SOME_ENTITY, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      editSomeEntity(someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: EDIT_SOME_ENTITY, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      syncSomeEntity(someEntity) -> {type: ADD_SOME_ENTITY_PENDING/EDIT_SOME_ENTITY_PENDING, someEntityID: someEntity.ID, someEntity },
            other network states
 *      removeSomeEntity(someEntityID) -> {type: DELETE_SOME_ENTITY_PENDING, someEntityID }, other network states
 *      voidAllSomeEntities() -> {type: VOID_ALL_SOME_ENTITIES}
 * @param {String} entityActionMiffix Name of an entity as it appears in action creator name: 'Entity' -> loadEntity
 * @param {String} entityActionTypeMiffix Name of an entity as it appears in action type name: 'ENTITY' -> LOAD_ENTITY
 * @param {String} entityPayloadMiffix Name of an entity as it appears in action payload: 'entity' -> { entityID }
 * @param {Object} API Instance of Swagger API Client.
 * @param {Object} APIMap Object which maps action creator prefix to API method name: {'loadAll': 'entityLoadAllGet'}. 
 *                        When map has no key for a certain action, no action will be created.
 *                        When true passed instead of API method name, default name will be constructed.
 *                        When null or undefined passed instead of map, all actions will be created with default API methods.
 * @param {bool} isReportable Convert parameters to work with ReportableEntityDataAccess methods
 * @returns {Object} Set of action creators.
 */
export function entityLifecycleActionSet(entityActionMiffix, entityActionTypeMiffix, entityPayloadMiffix, apiClassOrInstance, APIMap, isReportable = false) {
    const defaultAPIMap = {
        'loadAll': `${entityPayloadMiffix}LoadAllGet`,
        'load': `${entityPayloadMiffix}LoadGet`,
        'add': `${entityPayloadMiffix}AddPut`,
        'edit': `${entityPayloadMiffix}EditPut`,
        'delete': `${entityPayloadMiffix}DeleteGet`,
        'voidAll': false
    };

    APIMap = APIMap || defaultAPIMap;
    for (var i in APIMap) {
        if (APIMap[i] === true) {
            APIMap[i] = defaultAPIMap[i] || true;
        }
    }

    const idFieldName = `${entityPayloadMiffix}ID`,
        result = {};

    if (APIMap['loadAll']) {
        const loadAllPrefixName = `LOAD_ALL_${plural(entityActionTypeMiffix)}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['loadAll']);
            assertPrefixExists(loadAllPrefixName);
        }

        result[`loadAll${plural(entityActionMiffix)}`] =
            (...args) => networkAPIAction(apiClassOrInstance, APIMap['loadAll'], NetworkActionPrefix[loadAllPrefixName], {}, ...args);
    }

    let loadAction;
    if (APIMap['load']) {
        const loadPrefixName = `LOAD_${entityActionTypeMiffix}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['load']);
            assertPrefixExists(loadPrefixName);
        }

        loadAction = result[`load${entityActionMiffix}`] = (entityID, ...args) =>
            networkAPIAction(apiClassOrInstance, APIMap['load'], NetworkActionPrefix[loadPrefixName], { [idFieldName]: entityID }, entityID, ...args);
    }

    if (APIMap['add'] || APIMap['edit']) {
        const addPrefixName = `ADD_${entityActionTypeMiffix}_PREFIX`;
        const editPrefixName = `EDIT_${entityActionTypeMiffix}_PREFIX`;

        const syncAction = result[`sync${entityActionMiffix}`] =
            (entity) => isInternalID(entity.ID) ?
                networkAPIAction(apiClassOrInstance, APIMap['add'], NetworkActionPrefix[addPrefixName], { [idFieldName]: entity.ID, [entityPayloadMiffix]: entity }, entity) :
                networkAPIAction(apiClassOrInstance, APIMap['edit'], NetworkActionPrefix[editPrefixName], { [idFieldName]: entity.ID, [entityPayloadMiffix]: entity }, entity, ...(isReportable ? [] : [entity.ID]));

        if (APIMap['add']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['add']);
                assertPrefixExists(addPrefixName);
            }

            result[`add${entityActionMiffix}`] = (entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({ type: ActionType[NetworkActionPrefix[addPrefixName]], [idFieldName]: entity.ID, [entityPayloadMiffix]: entity });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(entity))
                        .then(response => dispatch(loadAction(response, ...reloadArgs)))
                        .then(resolve, reject);
                }
            })
        }

        if (APIMap['edit']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['edit']);
                assertPrefixExists(editPrefixName);
            }

            result[`edit${entityActionMiffix}`] = (entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({ type: ActionType[NetworkActionPrefix[editPrefixName]], [idFieldName]: entity.ID, [entityPayloadMiffix]: entity });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(entity))
                        .then(response => dispatch(loadAction(entity.ID, ...reloadArgs)))
                        .then(resolve, reject);
                }
            });
        }
    }
    if (APIMap['delete']) {
        const deletePrefixName = `DELETE_${entityActionTypeMiffix}_PREFIX`;
        const deleteSuccessActionName = `${NetworkActionPrefix[`DELETE_${entityActionTypeMiffix}_PREFIX`]}_SUCCESS`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['delete']);
            assertPrefixExists(deletePrefixName);
        }

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to remove entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to remove entity from DB if it has been added but its new ID has been lost in network failure?
        result[`remove${entityActionMiffix}`] = (entityID, ...args) =>
            isInternalID(entityID) ?
                { type: ActionType[deleteSuccessActionName], [idFieldName]: entityID } :
                networkAPIAction(apiClassOrInstance, APIMap['delete'], NetworkActionPrefix[deletePrefixName], { [idFieldName]: entityID }, entityID, ...args);
    }

    if (APIMap['voidAll']) {
        const voidAllActionName = `VOID_ALL_${plural(entityActionTypeMiffix)}`;

        if (DEBUG) {
            assertActionExists(voidAllActionName);
        }

        result[`voidAll${plural(entityActionMiffix)}`] = (immediately = true) => ({ type: ActionType[voidAllActionName], immediately });
    }

    return result;
};

/**
 * Creates a set of most used action creators for an entity stored in an property of another entity.
 *      loadAllSomeEntities(parentEntityID, ...args) -> {type: LOAD_ALL_SOME_ENTITIES, parentEntityID}
 *      loadSomeEntity(parentEntityID, someEntityID, ...args) -> {type: LOAD_SOME_ENTITY, parentEntityID, someEntityID}
 *      addSomeEntity(parentEntityID, someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: ADD_SOME_ENTITY, parentEntityID, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      editSomeEntity(parentEntityID, someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: EDIT_SOME_ENTITY, parentEntityID, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      syncSomeEntity(parentEntityID, someEntity) -> {type: ADD_SOME_ENTITY_PENDING/EDIT_SOME_ENTITY_PENDING, parentEntityID, someEntityID: someEntity.ID, someEntity },
            other network states
 *      removeSomeEntity(parentEntityID, someEntityID) -> {type: DELETE_SOME_ENTITY_PENDING, parentEntityID, someEntityID }, other network states
 *      voidAllSomeEntities(parentEntityID) -> {type: VOID_ALL_SOME_ENTITIES, parentEntityID}
 * @param {String} entityActionMiffix Name of an entity as it appears in action creator name: 'Entity' -> loadEntity
 * @param {String} entityActionTypeMiffix Name of an entity as it appears in action type name: 'ENTITY' -> LOAD_ENTITY
 * @param {String} parentEntityPayloadMiffix Name of an entity as it appears in action payload: 'parentEntity' -> { parentEntityID }
 * @param {String} entityPayloadMiffix Name of an entity as it appears in action payload: 'entity' -> { entityID }
 * @param {Object} API Instance of Swagger API Client.
 * @param {Object} APIMap Object which maps action creator prefix to API method name: {'loadAll': 'entityLoadAllGet'}. 
 *                        When map has no key for a certain action, no action will be created.
 *                        When true passed instead of API method name, default name will be constructed.
 *                        When null or undefined passed instead of map, all actions will be created with default API methods.
 * @param {bool} isReportable Convert parameters to work with ReportableEntityDataAccess methods
 * @returns {Object} Set of action creators.
 */
export function nestedEntityLifecycleActionSet(entityActionMiffix, entityActionTypeMiffix, parentEntityPayloadMiffix, entityPayloadMiffix, apiClassOrInstance, APIMap, isReportable = false) {
    const defaultAPIMap = {
        'loadAll': `${entityPayloadMiffix}LoadAllGet`,
        'load': `${entityPayloadMiffix}LoadGet`,
        'add': `${entityPayloadMiffix}AddPut`,
        'edit': `${entityPayloadMiffix}EditPut`,
        'delete': `${entityPayloadMiffix}DeleteGet`,
        'voidAll': false
    };

    APIMap = APIMap || defaultAPIMap;
    for (var i in APIMap) {
        if (APIMap[i] === true) {
            APIMap[i] = defaultAPIMap[i] || true;
        }
    }

    const parentIDFieldName = `${parentEntityPayloadMiffix}ID`,
        idFieldName = `${entityPayloadMiffix}ID`,
        result = {};

    if (APIMap['loadAll']) {
        const loadAllPrefixName = `LOAD_ALL_${plural(entityActionTypeMiffix)}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['loadAll']);
            assertPrefixExists(loadAllPrefixName);
        }

        result[`loadAll${plural(entityActionMiffix)}`] =
            (parentEntityID, ...args) => networkAPIAction(
                apiClassOrInstance,
                APIMap['loadAll'],
                NetworkActionPrefix[loadAllPrefixName],
                {
                    [parentIDFieldName]: parentEntityID
                },
                ...args
            );
    }

    let loadAction;
    if (APIMap['load']) {
        const loadPrefixName = `LOAD_${entityActionTypeMiffix}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['load']);
            assertPrefixExists(loadPrefixName);
        }

        loadAction = result[`load${entityActionMiffix}`] = (parentEntityID, entityID, ...args) =>
            networkAPIAction(
                apiClassOrInstance,
                APIMap['load'],
                NetworkActionPrefix[loadPrefixName],
                {
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entityID
                },
                entityID,
                ...args
            );
    }

    if (APIMap['add'] || APIMap['edit']) {
        const addPrefixName = `ADD_${entityActionTypeMiffix}_PREFIX`;
        const editPrefixName = `EDIT_${entityActionTypeMiffix}_PREFIX`;

        const syncAction = result[`sync${entityActionMiffix}`] =
            (parentEntityID, entity) => isInternalID(entity.ID) ?
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['add'],
                    NetworkActionPrefix[addPrefixName],
                    {
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entity.ID,
                        [entityPayloadMiffix]: entity
                    },
                    entity
                ) :
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['edit'],
                    NetworkActionPrefix[editPrefixName],
                    {
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entity.ID,
                        [entityPayloadMiffix]: entity
                    },
                    entity,
                    ...(isReportable ? [] : [entity.ID])
                );

        if (APIMap['add']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['add']);
                assertPrefixExists(addPrefixName);
            }

            result[`add${entityActionMiffix}`] = (parentEntityID, entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({
                    type: ActionType[NetworkActionPrefix[addPrefixName]],
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entity.ID,
                    [entityPayloadMiffix]: entity
                });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(parentEntityID, entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(parentEntityID, entity))
                        .then(response => dispatch(loadAction(parentEntityID, response, ...reloadArgs)))
                        .then(resolve, reject);
                }
            })
        }

        if (APIMap['edit']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['edit']);
                assertPrefixExists(editPrefixName);
            }

            result[`edit${entityActionMiffix}`] = (parentEntityID, entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({
                    type: ActionType[NetworkActionPrefix[editPrefixName]],
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entity.ID,
                    [entityPayloadMiffix]: entity
                });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(parentEntityID, entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(parentEntityID, entity))
                        .then(response => dispatch(loadAction(parentEntityID, entity.ID, ...reloadArgs)))
                        .then(resolve, reject);
                }
            });
        }
    }
    if (APIMap['delete']) {
        const deletePrefixName = `DELETE_${entityActionTypeMiffix}_PREFIX`;
        const deleteSuccessActionName = `${NetworkActionPrefix[`DELETE_${entityActionTypeMiffix}_PREFIX`]}_SUCCESS`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['delete']);
            assertPrefixExists(deletePrefixName);
        }

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to remove entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to remove entity from DB if it has been added but its new ID has been lost in network failure?
        result[`remove${entityActionMiffix}`] = (parentEntityID, entityID, ...args) =>
            isInternalID(entityID) ?
                {
                    type: ActionType[deleteSuccessActionName],
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entityID
                } :
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['delete'],
                    NetworkActionPrefix[deletePrefixName],
                    {
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entityID
                    },
                    entityID,
                    ...args
                );
    }

    if (APIMap['voidAll']) {
        const voidAllActionName = `VOID_ALL_${plural(entityActionTypeMiffix)}`;

        if (DEBUG) {
            assertActionExists(voidAllActionName);
        }

        result[`voidAll${plural(entityActionMiffix)}`] =
            (parentEntityID, immediately = true) => ({ type: ActionType[voidAllActionName], [parentIDFieldName]: parentEntityID, immediately });
    }

    return result;
};

/**
 * Creates a set of most used action creators for an entity stored in an property of another entity stored in an property of another entity.
 *      loadAllSomeEntities(grandParentEntityID, parentEntityID, ...args) -> {type: LOAD_ALL_SOME_ENTITIES, grandParentEntityID, parentEntityID}
 *      loadSomeEntity(grandParentEntityID, parentEntityID, someEntityID, ...args) -> {type: LOAD_SOME_ENTITY, grandParentEntityID, parentEntityID, someEntityID}
 *      addSomeEntity(grandParentEntityID, parentEntityID, someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: ADD_SOME_ENTITY, grandParentEntityID, parentEntityID, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      editSomeEntity(grandParentEntityID, parentEntityID, someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: EDIT_SOME_ENTITY, grandParentEntityID, parentEntityID, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      syncSomeEntity(grandParentEntityID, parentEntityID, someEntity) -> {type: ADD_SOME_ENTITY_PENDING/EDIT_SOME_ENTITY_PENDING, grandParentEntityID, parentEntityID, someEntityID: someEntity.ID, someEntity },
            other network states
 *      removeSomeEntity(grandParentEntityID, parentEntityID, someEntityID) -> {type: DELETE_SOME_ENTITY_PENDING, grandParentEntityID, parentEntityID, someEntityID }, other network states
 *      voidAllSomeEntities(grandParentEntityID, parentEntityID) -> {type: VOID_ALL_SOME_ENTITIES, grandParentEntityID, parentEntityID}
 * @param {String} entityActionMiffix Name of an entity as it appears in action creator name: 'Entity' -> loadEntity
 * @param {String} entityActionTypeMiffix Name of an entity as it appears in action type name: 'ENTITY' -> LOAD_ENTITY
 * @param {String} parentEntityPayloadMiffix Name of an entity as it appears in action payload: 'parentEntity' -> { grandParentEntityID, parentEntityID }
 * @param {String} entityPayloadMiffix Name of an entity as it appears in action payload: 'entity' -> { entityID }
 * @param {Object} API Instance of Swagger API Client.
 * @param {Object} APIMap Object which maps action creator prefix to API method name: {'loadAll': 'entityLoadAllGet'}. 
 *                        When map has no key for a certain action, no action will be created.
 *                        When true passed instead of API method name, default name will be constructed.
 *                        When null or undefined passed instead of map, all actions will be created with default API methods.
 * @param {bool} isReportable Convert parameters to work with ReportableEntityDataAccess methods
 * @returns {Object} Set of action creators.
 */
export function doubleNestedEntityLifecycleActionSet(entityActionMiffix, entityActionTypeMiffix, grandParentEntityPayloadMiffix, parentEntityPayloadMiffix, entityPayloadMiffix, apiClassOrInstance, APIMap, isReportable = false) {
    const defaultAPIMap = {
        'loadAll': `${entityPayloadMiffix}LoadAllGet`,
        'load': `${entityPayloadMiffix}LoadGet`,
        'add': `${entityPayloadMiffix}AddPut`,
        'edit': `${entityPayloadMiffix}EditPut`,
        'delete': `${entityPayloadMiffix}DeleteGet`,
        'voidAll': false
    };

    APIMap = APIMap || defaultAPIMap;
    for (var i in APIMap) {
        if (APIMap[i] === true) {
            APIMap[i] = defaultAPIMap[i] || true;
        }
    }

    const grandParentIDFieldName = `${grandParentEntityPayloadMiffix}ID`,
        parentIDFieldName = `${parentEntityPayloadMiffix}ID`,
        idFieldName = `${entityPayloadMiffix}ID`,
        result = {};

    if (APIMap['loadAll']) {
        const loadAllPrefixName = `LOAD_ALL_${plural(entityActionTypeMiffix)}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['loadAll']);
            assertPrefixExists(loadAllPrefixName);
        }

        result[`loadAll${plural(entityActionMiffix)}`] =
            (grandParentEntityID, parentEntityID, ...args) => networkAPIAction(
                apiClassOrInstance,
                APIMap['loadAll'],
                NetworkActionPrefix[loadAllPrefixName],
                {
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID
                },
                ...args
            );
    }

    let loadAction;
    if (APIMap['load']) {
        const loadPrefixName = `LOAD_${entityActionTypeMiffix}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['load']);
            assertPrefixExists(loadPrefixName);
        }

        loadAction = result[`load${entityActionMiffix}`] = (grandParentEntityID, parentEntityID, entityID, ...args) =>
            networkAPIAction(
                apiClassOrInstance,
                APIMap['load'],
                NetworkActionPrefix[loadPrefixName],
                {
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entityID
                },
                entityID,
                ...args
            );
    }

    if (APIMap['add'] || APIMap['edit']) {
        const addPrefixName = `ADD_${entityActionTypeMiffix}_PREFIX`;
        const editPrefixName = `EDIT_${entityActionTypeMiffix}_PREFIX`;

        const syncAction = result[`sync${entityActionMiffix}`] =
            (grandParentEntityID, parentEntityID, entity) => isInternalID(entity.ID) ?
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['add'],
                    NetworkActionPrefix[addPrefixName],
                    {
                        [grandParentIDFieldName]: grandParentEntityID,
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entity.ID,
                        [entityPayloadMiffix]: entity
                    },
                    entity
                ) :
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['edit'],
                    NetworkActionPrefix[editPrefixName],
                    {
                        [grandParentIDFieldName]: grandParentEntityID,
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entity.ID,
                        [entityPayloadMiffix]: entity
                    },
                    entity,
                    ...(isReportable ? [] : [entity.ID])
                );

        if (APIMap['add']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['add']);
                assertPrefixExists(addPrefixName);
            }

            result[`add${entityActionMiffix}`] = (grandParentEntityID, parentEntityID, entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({
                    type: ActionType[NetworkActionPrefix[addPrefixName]],
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entity.ID,
                    [entityPayloadMiffix]: entity
                });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(grandParentEntityID, parentEntityID, entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(grandParentEntityID, parentEntityID, entity))
                        .then(response => dispatch(loadAction(grandParentEntityID, parentEntityID, response, ...reloadArgs)))
                        .then(resolve, reject);
                }
            })
        }

        if (APIMap['edit']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['edit']);
                assertPrefixExists(editPrefixName);
            }

            result[`edit${entityActionMiffix}`] = (grandParentEntityID, parentEntityID, entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({
                    type: ActionType[NetworkActionPrefix[editPrefixName]],
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entity.ID,
                    [entityPayloadMiffix]: entity
                });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(grandParentEntityID, parentEntityID, entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(grandParentEntityID, parentEntityID, entity))
                        .then(response => loadAction(grandParentEntityID, parentEntityID, response, ...reloadArgs))
                        .then(resolve, reject);
                }
            });
        }
    }
    if (APIMap['delete']) {
        const deletePrefixName = `DELETE_${entityActionTypeMiffix}_PREFIX`;
        const deleteSuccessActionName = `${NetworkActionPrefix[`DELETE_${entityActionTypeMiffix}_PREFIX`]}_SUCCESS`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['delete']);
            assertPrefixExists(deletePrefixName);
        }

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to remove entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to remove entity from DB if it has been added but its new ID has been lost in network failure?
        result[`remove${entityActionMiffix}`] = (grandParentEntityID, parentEntityID, entityID, ...args) =>
            isInternalID(entityID) ?
                {
                    type: ActionType[deleteSuccessActionName],
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entityID
                } :
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['delete'],
                    NetworkActionPrefix[deletePrefixName],
                    {
                        [grandParentIDFieldName]: grandParentEntityID,
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entityID
                    },
                    entityID,
                    ...args
                );
    }

    if (APIMap['voidAll']) {
        const voidAllActionName = `VOID_ALL_${plural(entityActionTypeMiffix)}`;

        if (DEBUG) {
            assertActionExists(voidAllActionName);
        }

        result[`voidAll${plural(entityActionMiffix)}`] =
            (grandParentEntityID, parentEntityID, immediately = true) => ({
                type: ActionType[voidAllActionName],
                [grandParentIDFieldName]: grandParentEntityID,
                [parentIDFieldName]: parentEntityID,
                immediately
            });
    }

    return result;
};


/**
 * Creates a set of most used action creators for an entity stored somewhere deep in a tree of entities.
 *      loadAllSomeEntities(grandGrandParentEntityID, grandParentEntityID, parentEntityID, ...args) -> {type: LOAD_ALL_SOME_ENTITIES, grandGrandParentEntityID, grandParentEntityID, parentEntityID}
 *      loadSomeEntity(grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntityID, ...args) -> {type: LOAD_SOME_ENTITY, grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntityID}
 *      addSomeEntity(grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: ADD_SOME_ENTITY, grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      editSomeEntity(grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntity, sync, reloadAfterSync, ...reloadArgs) -> {type: EDIT_SOME_ENTITY, grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntityID: someEntity.ID, someEntity },
            call syncSomeEntity after, call loadSomeEntity(response, ...reloadArgs) after
 *      syncSomeEntity(grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntity) -> {type: ADD_SOME_ENTITY_PENDING/EDIT_SOME_ENTITY_PENDING, grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntityID: someEntity.ID, someEntity },
            other network states
 *      removeSomeEntity(grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntityID) -> {type: DELETE_SOME_ENTITY_PENDING, grandGrandParentEntityID, grandParentEntityID, parentEntityID, someEntityID }, other network states
 *      voidAllSomeEntities(grandGrandParentEntityID, grandParentEntityID, parentEntityID) -> {type: VOID_ALL_SOME_ENTITIES, grandGrandParentEntityID, grandParentEntityID, parentEntityID}
 * @param {String} entityActionMiffix Name of an entity as it appears in action creator name: 'Entity' -> loadEntity
 * @param {String} entityActionTypeMiffix Name of an entity as it appears in action type name: 'ENTITY' -> LOAD_ENTITY
 * @param {String} parentEntityPayloadMiffix Name of an entity as it appears in action payload: 'parentEntity' -> { grandGrandParentEntityID, grandParentEntityID, parentEntityID }
 * @param {String} entityPayloadMiffix Name of an entity as it appears in action payload: 'entity' -> { entityID }
 * @param {Object} API Instance of Swagger API Client.
 * @param {Object} APIMap Object which maps action creator prefix to API method name: {'loadAll': 'entityLoadAllGet'}. 
 *                        When map has no key for a certain action, no action will be created.
 *                        When true passed instead of API method name, default name will be constructed.
 *                        When null or undefined passed instead of map, all actions will be created with default API methods.
 * @param {bool} isReportable Convert parameters to work with ReportableEntityDataAccess methods
 * @returns {Object} Set of action creators.
 */
export function tripleNestedEntityLifecycleActionSet(entityActionMiffix, entityActionTypeMiffix, grandGrandParentEntityPayloadMiffix, grandParentEntityPayloadMiffix, parentEntityPayloadMiffix, entityPayloadMiffix, apiClassOrInstance, APIMap, isReportable = false) {
    const defaultAPIMap = {
        'loadAll': `${entityPayloadMiffix}LoadAllGet`,
        'load': `${entityPayloadMiffix}LoadGet`,
        'add': `${entityPayloadMiffix}AddPut`,
        'edit': `${entityPayloadMiffix}EditPut`,
        'delete': `${entityPayloadMiffix}DeleteGet`,
        'voidAll': false
    };

    APIMap = APIMap || defaultAPIMap;
    for (var i in APIMap) {
        if (APIMap[i] === true) {
            APIMap[i] = defaultAPIMap[i] || true;
        }
    }

    const grandGrandParentIDFieldName = `${grandGrandParentEntityPayloadMiffix}ID`,
        grandParentIDFieldName = `${grandParentEntityPayloadMiffix}ID`,
        parentIDFieldName = `${parentEntityPayloadMiffix}ID`,
        idFieldName = `${entityPayloadMiffix}ID`,
        result = {};

    if (APIMap['loadAll']) {
        const loadAllPrefixName = `LOAD_ALL_${plural(entityActionTypeMiffix)}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['loadAll']);
            assertPrefixExists(loadAllPrefixName);
        }

        result[`loadAll${plural(entityActionMiffix)}`] =
            (grandGrandParentEntityID, grandParentEntityID, parentEntityID, ...args) => networkAPIAction(
                apiClassOrInstance,
                APIMap['loadAll'],
                NetworkActionPrefix[loadAllPrefixName],
                {
                    [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID
                },
                ...args
            );
    }

    let loadAction;
    if (APIMap['load']) {
        const loadPrefixName = `LOAD_${entityActionTypeMiffix}_PREFIX`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['load']);
            assertPrefixExists(loadPrefixName);
        }

        loadAction = result[`load${entityActionMiffix}`] = (grandGrandParentEntityID, grandParentEntityID, parentEntityID, entityID, ...args) =>
            networkAPIAction(
                apiClassOrInstance,
                APIMap['load'],
                NetworkActionPrefix[loadPrefixName],
                {
                    [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entityID
                },
                entityID,
                ...args
            );
    }

    if (APIMap['add'] || APIMap['edit']) {
        const addPrefixName = `ADD_${entityActionTypeMiffix}_PREFIX`;
        const editPrefixName = `EDIT_${entityActionTypeMiffix}_PREFIX`;

        const syncAction = result[`sync${entityActionMiffix}`] =
            (grandGrandParentEntityID, grandParentEntityID, parentEntityID, entity) => isInternalID(entity.ID) ?
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['add'],
                    NetworkActionPrefix[addPrefixName],
                    {
                        [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                        [grandParentIDFieldName]: grandParentEntityID,
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entity.ID,
                        [entityPayloadMiffix]: entity
                    },
                    entity
                ) :
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['edit'],
                    NetworkActionPrefix[editPrefixName],
                    {
                        [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                        [grandParentIDFieldName]: grandParentEntityID,
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entity.ID,
                        [entityPayloadMiffix]: entity
                    },
                    entity,
                    ...(isReportable ? [] : [entity.ID])
                );

        if (APIMap['add']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['add']);
                assertPrefixExists(addPrefixName);
            }

            result[`add${entityActionMiffix}`] = (grandGrandParentEntityID, grandParentEntityID, parentEntityID, entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({
                    type: ActionType[NetworkActionPrefix[addPrefixName]],
                    [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entity.ID,
                    [entityPayloadMiffix]: entity
                });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(grandGrandParentEntityID, grandParentEntityID, parentEntityID, entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(grandGrandParentEntityID, grandParentEntityID, parentEntityID, entity))
                        .then(response => dispatch(loadAction(grandGrandParentEntityID, grandParentEntityID, parentEntityID, response, ...reloadArgs)))
                        .then(resolve, reject);
                }
            })
        }

        if (APIMap['edit']) {
            if (DEBUG) {
                assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['edit']);
                assertPrefixExists(editPrefixName);
            }

            result[`edit${entityActionMiffix}`] = (grandGrandParentEntityID, grandParentEntityID, parentEntityID, entity, sync, reloadAfterSync, ...reloadArgs) => dispatch => new Promise((resolve, reject) => {
                dispatch({
                    type: ActionType[NetworkActionPrefix[editPrefixName]],
                    [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entity.ID,
                    [entityPayloadMiffix]: entity
                });

                if (!sync) {
                    resolve(entity.ID);
                }
                else if (!reloadAfterSync || !DEBUG && !loadAction) {
                    dispatch(syncAction(grandGrandParentEntityID, grandParentEntityID, parentEntityID, entity)).then(resolve, reject);
                }
                else {
                    if (DEBUG && !loadAction) {
                        throw new Error(`Can't reload after sync - APIMap.load is not configured.`)
                    }

                    dispatch(syncAction(grandGrandParentEntityID, grandParentEntityID, parentEntityID, entity))
                        .then(response => loadAction(grandGrandParentEntityID, grandParentEntityID, parentEntityID, response, ...reloadArgs))
                        .then(resolve, reject);
                }
            });
        }
    }
    if (APIMap['delete']) {
        const deletePrefixName = `DELETE_${entityActionTypeMiffix}_PREFIX`;
        const deleteSuccessActionName = `${NetworkActionPrefix[`DELETE_${entityActionTypeMiffix}_PREFIX`]}_SUCCESS`;

        if (DEBUG) {
            assertAPIEntryExists(entityActionMiffix, apiClassOrInstance, APIMap['delete']);
            assertPrefixExists(deletePrefixName);
        }

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to remove entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to remove entity from DB if it has been added but its new ID has been lost in network failure?
        result[`remove${entityActionMiffix}`] = (grandGrandParentEntityID, grandParentEntityID, parentEntityID, entityID, ...args) =>
            isInternalID(entityID) ?
                {
                    type: ActionType[deleteSuccessActionName],
                    [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                    [grandParentIDFieldName]: grandParentEntityID,
                    [parentIDFieldName]: parentEntityID,
                    [idFieldName]: entityID
                } :
                networkAPIAction(
                    apiClassOrInstance,
                    APIMap['delete'],
                    NetworkActionPrefix[deletePrefixName],
                    {
                        [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                        [grandParentIDFieldName]: grandParentEntityID,
                        [parentIDFieldName]: parentEntityID,
                        [idFieldName]: entityID
                    },
                    entityID,
                    ...args
                );
    }

    if (APIMap['voidAll']) {
        const voidAllActionName = `VOID_ALL_${plural(entityActionTypeMiffix)}`;

        if (DEBUG) {
            assertActionExists(voidAllActionName);
        }

        result[`voidAll${plural(entityActionMiffix)}`] =
            (grandGrandParentEntityID, grandParentEntityID, parentEntityID, immediately = true) => ({
                type: ActionType[voidAllActionName],
                [grandGrandParentIDFieldName]: grandGrandParentEntityID,
                [grandParentIDFieldName]: grandParentEntityID,
                [parentIDFieldName]: parentEntityID,
                immediately
            });
    }

    return result;
};

export function sequence(...actions) {
    if (actions.length < 1) {
        throw new Error('Actions sequence can\'t be empty.');
    }
    return dispatch => {
        let i = 0;

        let makeSequenceStep = (params) => {
            let promise = dispatch(actions[i]);
            if (i < actions.length - 1 && !(promise instanceof Promise)) {
                throw new Error('All actions except last one must return a Promise.');
            } else if (i < actions.length - 1) {
                promise.then((result) => {
                    i++;
                    if (i >= actions.length) {
                        return;
                    }
                    return makeSequenceStep()
                })
            }
        };

        makeSequenceStep();
    }
}